import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

// Context
import { useCoursesNewContext } from "../../context";

// Draggable
import { DndContext, useDroppable } from "@dnd-kit/core";

// Components
import CustomButton from "../../../../../components/CustomButton";
import Spinner from "../../../../../components/spinner";

// Specific Components
import AddToStructure from "./AddToStructure";
import UnitRow from "./UnitRow";
import KeyDateRow from "./KeyDateRow";

// Styles
import styles from "./style.module.scss";
import stepStyles from "../steps.module.scss";

// Types
import { Unit, KeyDate } from "../../../../../types";

// Utils
import { unitsUrl, unitUrl, courseKeyDatesUrl, courseKeyDateUrl } from "../../../../../utils/urls";
import useCsrfToken from "../../../../../utils/hooks/csfrToken";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";

interface StructureProps {
  onFinish: () => void;
  onPrev: () => void;
}

type StructureRow = (Unit | KeyDate) & { type: 'unit' | 'key_date', key: string };

const Structure: React.FC<StructureProps> = ({ onFinish, onPrev }) => {
  // Hooks
  const { t } = useTranslation();
  const csrfToken = useCsrfToken();

  // Context
  const { course } = useCoursesNewContext();

  // Drag and Drop
  const { setNodeRef: setNodeRefDroppable } = useDroppable({ id: 'course_units' });

  // State
  const [structure, setStructure] = useState<StructureRow[]>([]);
  const [courseDepth, setCourseDepth] = useState(course?.course_depth || 50);

  // Structure Modal
  const [openEventModal, setOpenEventModal] = useState(false);
  const [eventType, setEventType] = useState<'unit' | 'key_date' | undefined>(undefined);
  const [eventData, setEventData] = useState<Unit | KeyDate | undefined>(undefined);

  const [loading, setLoading] = useState(false);


  // Methods
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const options = {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      };

      let units = [];
      let keyDates = [];

      const responseUnits = await fetch(unitsUrl(course?.id || ''), options);
      if (responseUnits.ok) {
        const data = await responseUnits.json();
        units = data;
      }

      const responseKeyDates = await fetch(courseKeyDatesUrl(course?.id || ''), options);
      if (responseKeyDates.ok) {
        const data = await responseKeyDates.json();
        keyDates = data;
      }

      setStructure([
        ...units.map((unit: Unit) => ({ ...unit, type: 'unit', key: `unit_${unit.id.toString()}` })),
        ...keyDates.map((keyDate: KeyDate) => ({ ...keyDate, type: 'key_date', key: `key_date_${keyDate.id.toString()}` }))
      ]);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  // const getUnits = useCallback(async () => {
  //   setLoading(true);
  //   try {
  //     const options = {
  //       method: 'GET',
  //       headers: {
  //         'Accept': 'application/json',
  //       },
  //     };

  //     const response = await fetch(unitsUrl(course?.id || ''), options);
  //     if (response.ok) {
  //       const data = await response.json();
  //       setUnits(data);
  //     }
  //   } catch (error) {
  //     console.error(error);
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [course?.id]);

  // const getKeyDates = useCallback(async () => {
  //   setLoading(true);
  //   try {
  //     const options = {
  //       method: 'GET',
  //       headers: {
  //         'Accept': 'application/json'
  //       },
  //     };

  //     const response = await fetch(courseKeyDatesUrl(course?.id || ''), options);
  //     if (response.ok) {
  //       const data = await response.json();
  //       setKeyDates(data);
  //     }
  //   } catch (error) {
  //     console.error(error);
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [course?.id]);

  const deleteKeyDate = useCallback(async (id: string | number) => {
    setLoading(true);
    try {
      const options = {
        method: 'DELETE',
        headers: {
          'X-CSRF-TOKEN': csrfToken,
        },
      };

      const response = await fetch(courseKeyDateUrl(course?.id || '', id), options);
      if (response.ok) {
        return true;
      } else {
        throw new Error('Failed to delete key date');
      }
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [course?.id, csrfToken]);

  const deleteUnit = useCallback(async (unitId: string | number) => {
    setLoading(true);
    try {
      const options = {
        method: 'DELETE',
        headers: { 'X-CSRF-TOKEN': csrfToken },
      };

      const response = await fetch(unitUrl(course?.id || '', unitId), options);
      if (response.ok) {
        return true;
      } else {
        throw new Error('Failed to delete unit');
      }
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [course?.id, csrfToken]);

  // useEffect
  useEffect(() => {
    loadData();
  }, []);

  // Handlers
  const handleFinish = useCallback(() => {
    onFinish();
  }, [onFinish]);

  const handlePrev = useCallback(() => {
    onPrev();
  }, [onPrev]);

  const handleAddEvent = useCallback(() => {
    setOpenEventModal(true);
  }, []);

  const handleCloseEventModal = useCallback(() => {
    setOpenEventModal(false);
    setEventType(undefined);
    setEventData(undefined);
  }, []);

  const handleCourseDepthChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCourseDepth(Number(e.target.value));
  }, []);

  const handleSaveStructure = useCallback(async (success: boolean) => {
    if (success) {
      handleCloseEventModal();
      loadData();
    }
  }, [loadData, handleCloseEventModal]);

  const handleEditUnit = useCallback(async (id: string | number) => {
    const unit = structure.find((row) => row.type === 'unit' && row.id === id);
    if (unit) {
      setOpenEventModal(true);
      setEventType('unit');
      setEventData(unit);
    }
  }, [structure]);

  const handleDeleteUnit = useCallback(async (id: string | number) => {
    const success = await deleteUnit(id);
    if (success) await loadData();
  }, [deleteUnit, loadData]);

  const handleEditKeyDate = useCallback(async (id: string | number) => {
    const keyDate = structure.find((row) => row.type === 'key_date' && row.id === id);
    if (keyDate) {
      setOpenEventModal(true);
      setEventType('key_date');
      setEventData(keyDate);
    }
  }, [structure]);

  const handleDeleteKeyDate = useCallback(async (id: string | number) => {
    const success = await deleteKeyDate(id);
    if (success) await loadData();
  }, [deleteKeyDate, loadData]);

  const handleDragEnd = useCallback((result: any) => {
    const { active, over } = result;
    if (active.id !== over.id) {
      const oldIndex = structure.findIndex(row => row.key === active.id);
      const newIndex = structure.findIndex(row => row.key === over.id);
      const newStructure = arrayMove(structure, oldIndex, newIndex);
      setStructure(newStructure);
    }
  }, [structure]);

  // Render
  const renderEvents = useCallback(() => {
    return structure.map((event, index) => {
      if (event.type === 'unit') {
        return (
          <UnitRow
            index={index}
            key={event.id}
            unit={event as Unit}
            onDelete={handleDeleteUnit}
            onEdit={handleEditUnit}
          />
        );
      } else {
        return (
          <KeyDateRow
            key={event.id}
            keyDate={event as KeyDate}
            onEdit={handleEditKeyDate}
            onDelete={handleDeleteKeyDate}
          />
        );
      }
    })
  }, [structure, deleteUnit, handleEditUnit, handleEditKeyDate, handleDeleteKeyDate, handleDeleteUnit]);

  const renderCourseDepthLabel = useCallback(() => {
    if (courseDepth >= 0 && courseDepth <= 30) return t('courses.new.structure.course_depth.shallow');
    else if (courseDepth > 30 && courseDepth <= 40) return t('courses.new.structure.course_depth.medium');
    else if (courseDepth > 40 && courseDepth <= 60) return t('courses.new.structure.course_depth.balanced');
    else if (courseDepth > 60 && courseDepth <= 70) return t('courses.new.structure.course_depth.advanced');
    else if (courseDepth > 70 && courseDepth <= 100) return t('courses.new.structure.course_depth.deep');
  }, [courseDepth]);

  return (
    <div className={stepStyles.root}>
      <div className={stepStyles.content}>
        <h1 className='text-2xl font-bold text-black dark:text-white'>
          {t('courses.new.structure.title')}
        </h1>

        <p className='mb-4 text-md text-gray-500 dark:text-gray-400'>
          {t('courses.new.structure.subtitle')}
        </p>

        <div className={styles.courseDepth}>
          <label
            htmlFor="course_depth"
            className='text-sm text-gray-500 dark:text-gray-400 w-full text-center'
          >
            {t('courses.new.structure.course_depth_label')}
          </label>

          <input
            type="range"
            min="0"
            max="100"
            step="5"
            id="course_depth"
            value={courseDepth}
            onChange={handleCourseDepthChange}
            className={styles.rangeSlider}
          />

          <p className='text-sm text-gray-500 dark:text-gray-400'>
            {renderCourseDepthLabel()}
          </p>
        </div>

        <DndContext onDragEnd={handleDragEnd}>
          <SortableContext
            items={structure.map((row) => row.key)}
            strategy={verticalListSortingStrategy}
          >
            <div ref={setNodeRefDroppable} className={styles.events}>
              {loading && (<Spinner />)}

              {renderEvents()}

              <CustomButton
                variant='text'
                classes={{
                  root: `${styles.addEventButton} text-black dark:text-white`
                }}
                onClick={handleAddEvent}
              >
                {`+ ${t('actions.add')}`}
              </CustomButton>
            </div>
          </SortableContext>
        </DndContext>
      </div>

      <div className={stepStyles.actions}>
        <CustomButton
          classes={{
            root: stepStyles.prevButton
          }}
          variant='secondary'
          onClick={handlePrev}
        >
          {t('actions.previous')}
        </CustomButton>

        <CustomButton
          classes={{
            root: stepStyles.nextButton
          }}
          variant='primary'
          onClick={handleFinish}
        >
          {t('courses.new.finish')}
        </CustomButton>
      </div>

      <AddToStructure
        open={openEventModal}
        date_type={eventType}
        data={eventData}
        onClose={handleCloseEventModal}
        onSave={handleSaveStructure}
      />
    </div>
  )
};

export default Structure;
